/**
 * Hook para auditoria de limites de tenants
 * Busca dados de todos os tenants comparando recursos contratados vs uso real
 * Inclui funções para correção de discrepâncias (desativar membros/inboxes)
 * 
 * Versão: 1.1.0
 * Data: 2026-01-08
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('AdminTenantLimitsAudit');

// Interface de dados retornados pela RPC
export interface TenantLimitAudit {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  plan_id: string;
  plan_name: string;
  
  // Limites do plano
  plan_max_agents: number;
  plan_max_inboxes: number;
  plan_max_channels: number;
  
  // Add-ons
  addon_agents: number;
  addon_inboxes: number;
  
  // Limites efetivos
  effective_max_agents: number;
  effective_max_inboxes: number;
  
  // Uso real
  current_members: number;
  current_active_inboxes: number;
  
  // v1.0.2: Membros inativos (resultado de correções)
  inactive_members: number;
  
  // Discrepâncias
  members_over_limit: number;
  inboxes_over_limit: number;
  has_discrepancy: boolean;
}

// Interface de estatísticas resumidas
export interface AuditSummary {
  total_tenants: number;
  tenants_with_discrepancy: number;
  tenants_compliant: number;
  total_members_over: number;
  total_inboxes_over: number;
}

/**
 * Hook principal para buscar dados de auditoria de limites
 */
export function useAdminTenantLimitsAudit() {
  return useQuery({
    queryKey: ['admin-tenant-limits-audit'],
    queryFn: async (): Promise<TenantLimitAudit[]> => {
      logger.debug('Buscando dados de auditoria de limites...');
      
      const { data, error } = await supabase.rpc('get_admin_tenant_limits_audit');
      
      if (error) {
        logger.error('Erro ao buscar auditoria:', error);
        throw error;
      }
      
      logger.info(`Auditoria concluída: ${data?.length || 0} tenants analisados`);
      return (data || []) as TenantLimitAudit[];
    },
    staleTime: 60 * 1000, // 1 minuto
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook para calcular estatísticas resumidas da auditoria
 */
export function useAuditSummary(data: TenantLimitAudit[] | undefined): AuditSummary {
  if (!data || data.length === 0) {
    return {
      total_tenants: 0,
      tenants_with_discrepancy: 0,
      tenants_compliant: 0,
      total_members_over: 0,
      total_inboxes_over: 0,
    };
  }

  const tenantsWithDiscrepancy = data.filter(t => t.has_discrepancy);
  
  return {
    total_tenants: data.length,
    tenants_with_discrepancy: tenantsWithDiscrepancy.length,
    tenants_compliant: data.length - tenantsWithDiscrepancy.length,
    total_members_over: data.reduce((acc, t) => acc + t.members_over_limit, 0),
    total_inboxes_over: data.reduce((acc, t) => acc + t.inboxes_over_limit, 0),
  };
}

// =====================================================
// Interfaces para busca de membros/inboxes
// =====================================================

export interface TenantMemberForAudit {
  member_id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  is_owner: boolean;
  is_active: boolean;
  joined_at: string;
  can_deactivate: boolean;
}

export interface TenantInboxForAudit {
  inbox_id: string;
  name: string;
  channel_type: string;
  provider_slug: string;
  status: string;
  is_default: boolean;
  created_at: string;
  can_deactivate: boolean;
}

// =====================================================
// Hooks para buscar membros/inboxes para correção
// =====================================================

/**
 * Hook para buscar membros de um tenant para auditoria
 */
export function useTenantMembersForAudit(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant-members-audit', tenantId],
    queryFn: async (): Promise<TenantMemberForAudit[]> => {
      if (!tenantId) return [];
      
      logger.debug('Buscando membros do tenant para auditoria:', tenantId);
      
      const { data, error } = await supabase.rpc('get_tenant_members_for_audit', {
        p_tenant_id: tenantId,
      });
      
      if (error) {
        logger.error('Erro ao buscar membros:', error);
        throw error;
      }
      
      return (data || []) as TenantMemberForAudit[];
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 segundos
  });
}

/**
 * Hook para buscar inboxes de um tenant para auditoria
 */
export function useTenantInboxesForAudit(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant-inboxes-audit', tenantId],
    queryFn: async (): Promise<TenantInboxForAudit[]> => {
      if (!tenantId) return [];
      
      logger.debug('Buscando inboxes do tenant para auditoria:', tenantId);
      
      const { data, error } = await supabase.rpc('get_tenant_inboxes_for_audit', {
        p_tenant_id: tenantId,
      });
      
      if (error) {
        logger.error('Erro ao buscar inboxes:', error);
        throw error;
      }
      
      return (data || []) as TenantInboxForAudit[];
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 segundos
  });
}

// =====================================================
// Mutations para desativar membros/inboxes
// =====================================================

/**
 * Hook para desativar membros selecionados
 */
export function useDeactivateMembers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ memberIds, tenantId }: { memberIds: string[]; tenantId: string }) => {
      logger.info('Desativando membros:', { count: memberIds.length, tenantId });
      
      const { data, error } = await supabase.rpc('admin_deactivate_members', {
        p_member_ids: memberIds,
        p_tenant_id: tenantId,
      });
      
      if (error) {
        logger.error('Erro ao desativar membros:', error);
        throw error;
      }
      
      return data as { success: boolean; deactivated_count: number };
    },
    onSuccess: (data, variables) => {
      logger.info('Membros desativados com sucesso:', data);
      toast.success(`${data.deactivated_count} membro(s) desativado(s) com sucesso`);
      
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-limits-audit'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-members-audit', variables.tenantId] });
    },
    onError: (error: Error) => {
      logger.error('Falha ao desativar membros:', error);
      toast.error(`Erro ao desativar membros: ${error.message}`);
    },
  });
}

/**
 * Hook para suspender inboxes selecionadas
 */
export function useSuspendInboxes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ inboxIds, tenantId }: { inboxIds: string[]; tenantId: string }) => {
      logger.info('Suspendendo inboxes:', { count: inboxIds.length, tenantId });
      
      const { data, error } = await supabase.rpc('admin_suspend_inboxes', {
        p_inbox_ids: inboxIds,
        p_tenant_id: tenantId,
      });
      
      if (error) {
        logger.error('Erro ao suspender inboxes:', error);
        throw error;
      }
      
      return data as { success: boolean; suspended_count: number };
    },
    onSuccess: (data, variables) => {
      logger.info('Inboxes suspensas com sucesso:', data);
      toast.success(`${data.suspended_count} inbox(es) suspensa(s) com sucesso`);
      
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-limits-audit'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-inboxes-audit', variables.tenantId] });
    },
    onError: (error: Error) => {
      logger.error('Falha ao suspender inboxes:', error);
      toast.error(`Erro ao suspender inboxes: ${error.message}`);
    },
  });
}
